import { useEffect, useState } from "react";
import { supabase } from "../client/client";
import { Link, useNavigate } from "react-router-dom";
import { useLogin } from "../context/LoginContext";
import Loading from "../components/Loading";
import Layout from "../components/Layout";
import Dashboard from "./Dashboard";

const tiles = [
  {
    to: "/perfil",
    label: "Perfil",
    description: "Ver y editar tu información",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
      </svg>
    ),
    admin: false,
  },
  {
    to: "/area",
    label: "Área",
    description: "Equipos de tu área asignada",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.429 9.75 2.25 12l4.179 2.25m0-4.5 5.571 3 5.571-3m-11.142 0L2.25 7.5 12 2.25l9.75 5.25-4.179 2.25m0 0L21.75 12l-4.179 2.25m0 0 4.179 2.25L12 21.75 2.25 16.5l4.179-2.25m11.142 0-5.571 3-5.571-3" />
      </svg>
    ),
    admin: false,
  },
  {
    to: "/equipos",
    label: "Equipos",
    description: "Gestión de todos los equipos",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 0 1-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0 1 15 18.257V17.25m6-12V15a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 15V5.25m18 0A2.25 2.25 0 0 0 18.75 3H5.25A2.25 2.25 0 0 0 3 5.25m18 0V12a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 12V5.25" />
      </svg>
    ),
    admin: true,
  },
  {
    to: "/usuarios",
    label: "Usuarios",
    description: "Administrar usuarios del sistema",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
      </svg>
    ),
    admin: true,
  },
];

const HomePage = () => {
  const [loading, setLoading]   = useState(true);
  const [userData, setUserData] = useState(null);
  const { getSessionAuth }      = useLogin();
  const navigate                = useNavigate();

  useEffect(() => {
    const sessionAuth = async () => {
      const session = await getSessionAuth();
      if (session) {
        await getUser(session.id);
        setLoading(false);
      } else {
        navigate("/", { replace: true });
      }
    };
    sessionAuth();
  }, []);

  const getUser = async (id) => {
    const { error, data } = await supabase
      .from("users").select().eq("id", id).single();
    if (error) throw error.message;
    setUserData(data);
  };

  const isAdmin = userData?.role === "admin";
  const visibleTiles = tiles.filter((t) => !t.admin || isAdmin);

  return (
    <Layout>
      {loading && <Loading />}

      <div className="mb-8">
        <p className="text-[#4691A6] font-semibold text-sm uppercase tracking-wide">Bienvenido/a</p>
        <h1 className="text-gray-800 font-bold text-3xl mt-1">
          {userData?.name || "—"}
        </h1>
        <p className="text-gray-500 text-sm mt-2">
          Accede a las secciones disponibles en el panel de control.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        {visibleTiles.map((tile) => (
          <Link
            key={tile.to}
            to={tile.to}
            className="group flex flex-col items-center gap-3 bg-white border border-gray-200 rounded-xl p-6 hover:border-[#4691A6] hover:shadow-md transition-all duration-200"
          >
            <div className="text-[#356674] group-hover:text-[#4691A6] transition-colors duration-200">
              {tile.icon}
            </div>
            <div className="text-center">
              <p className="font-semibold text-gray-800 text-sm">{tile.label}</p>
              <p className="text-gray-400 text-xs mt-0.5">{tile.description}</p>
            </div>
          </Link>
        ))}
      </div>

      {isAdmin && (
        <div className="mt-10">
          <Dashboard standalone={false} />
        </div>
      )}
    </Layout>
  );
};

export default HomePage;
